import { useEffect, useMemo, useRef, useState } from "react";
import { useSnapshot } from "valtio";
import Loader from "src/components/common/loader/loader";
import { launchActions, launchStore } from "src/modules/engagement/launchStore";
import { workflowEvents } from "src/modules/engagement/workflow/workflowEvents";
import { LaunchNetworkService } from "@services/engagements/launch/launchNetworkService";
import { NetworkAccountService } from "@services/contentNetwork/networkAccounts/networkAccountService";
import { NetworkTemplateService } from "@services/contentNetwork/networkTemplates/networkTemplateService";
import {
  fetchReportingNetworkConfigs,
  type ReportingNetworkConfig,
} from "@services/referenceData/reportingNetworkConfigService";
import type { LaunchNetworkConfig } from "@models/launchNetwork.model";
import { NetworkResourceSelectorModal } from "./NetworkAccountSelectorModal";
import { NetworkTemplatePickerModal } from "./NetworkTemplatePickerModal";
import { InspectLinksModal } from "./InspectLinksModal";
import PublisherRow from "./PublisherRow";

type Picker = { kind: "resource" | "template" | "links"; network: string } | null;

const EMPTY_CONFIG = (network: string): LaunchNetworkConfig => ({
  network,
  is_enabled: false,
  network_template_id: null,
  network_resource_id: null,
  network_sub_resource_id: null,
});

export default function PublishersCard({ launchId }: { launchId: number }) {
  const { networkConfigs, isLoadingNetworks } = useSnapshot(launchStore);

  const [referenceConfigs, setReferenceConfigs] = useState<ReportingNetworkConfig[]>([]);
  const [isLoadingReference, setIsLoadingReference] = useState(true);
  const [picker, setPicker] = useState<Picker>(null);
  const [busyNetwork, setBusyNetwork] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [resourceNames, setResourceNames] = useState<Record<number, string>>({});
  const [templateNames, setTemplateNames] = useState<Record<number, string>>({});

  const networkService = useMemo(() => new LaunchNetworkService(), []);
  // Set by onTemplateSelected just before the modal calls onClose, so a plain cancel
  // (close with nothing picked) doesn't trigger a pointless refetch.
  const templateWasSelected = useRef(false);

  useEffect(() => {
    fetchReportingNetworkConfigs()
      .then(setReferenceConfigs)
      .catch(() => setError("Could not load the publisher list."))
      .finally(() => setIsLoadingReference(false));
  }, []);

  // One row per known publisher network, whether or not this campaign has a config yet.
  const rows = useMemo(() => {
    const seen = new Set<string>();
    const unique: { reference: ReportingNetworkConfig; network: string }[] = [];
    for (const reference of referenceConfigs) {
      // Direct delivery channels are in this list so they can be drawn on charts, but they
      // are not publishers: they have no ad account, no bidding and no template, which is
      // every column this card renders. They get their own card instead.
      const network = reference.publisher_network;
      if (!network || seen.has(network)) continue;
      seen.add(network);
      unique.push({ reference, network });
    }
    return unique.map(({ reference, network }) => ({
      reference,
      network,
      config:
        networkConfigs.find((c) => c.network.toLowerCase() === network.toLowerCase()) ??
        EMPTY_CONFIG(network),
    }));
  }, [referenceConfigs, networkConfigs]);

  // Resolve the IDs on the configs to names for display. A lookup can fail outright, or
  // simply not return every id we asked for; either way we fall back to a plain
  // identifier rather than leaving the field stuck on "Loading..." forever.
  useEffect(() => {
    const ids = rows
      .flatMap(({ config }) => [config.network_sub_resource_id, config.network_resource_id])
      .filter((id): id is number => !!id && !(id in resourceNames));
    if (!ids.length) return;
    (async () => {
      const resources = await new NetworkAccountService()
        .listNetworkResources({ resource_ids: ids })
        .catch(() => []);
      setResourceNames((prev) => {
        const next: Record<number, string> = {
          ...prev,
          ...Object.fromEntries(resources.map((r) => [r.id, r.name] as const)),
        };
        ids.forEach((id) => {
          if (!(id in next)) next[id] = `Resource #${id}`;
        });
        return next;
      });
    })();
  }, [rows]);

  useEffect(() => {
    const pending = rows.filter(
      ({ config }) => config.network_template_id && !(config.network_template_id in templateNames),
    );
    if (!pending.length) return;
    const service = new NetworkTemplateService();
    (async () => {
      const lists = await Promise.all(
        pending.map(({ network }) => service.list(network).catch(() => [])),
      );
      setTemplateNames((prev) => {
        const next: Record<number, string> = {
          ...prev,
          ...Object.fromEntries(lists.flat().map((t) => [t.id, t.name] as const)),
        };
        pending.forEach(({ config }) => {
          const id = config.network_template_id;
          if (id && !(id in next)) next[id] = `Template #${id}`;
        });
        return next;
      });
    })();
  }, [rows]);

  const liveCount = rows.filter(
    ({ config }) =>
      config.is_enabled && config.network_resource_id && config.network_template_id,
  ).length;

  const refreshConfigs = async () => {
    const fresh = await networkService.getConfigs(launchId);
    launchActions.setNetworkConfigs(fresh);
    workflowEvents.send_event("WORKFLOW_DATA_TOUCHED");
  };

  const handleToggle = async (network: string, nextEnabled: boolean) => {
    setBusyNetwork(network);
    setError(null);
    try {
      const updated = await networkService.toggle(launchId, network, nextEnabled);
      launchActions.updateNetworkConfig(network, { is_enabled: updated.is_enabled });
      workflowEvents.send_event("WORKFLOW_DATA_TOUCHED");
    } catch {
      setError(`Could not turn ${network} ${nextEnabled ? "on" : "off"}. Try again.`);
    } finally {
      setBusyNetwork(null);
    }
  };

  const handleResourceSelected = async (
    network: string,
    resourceId: number,
    subResourceId?: number,
  ) => {
    setBusyNetwork(network);
    setError(null);
    try {
      await networkService.updateAccount(launchId, network, resourceId, subResourceId);
      await refreshConfigs();
    } catch {
      setError(`Could not set the resource for ${network}. Try again.`);
    } finally {
      setBusyNetwork(null);
    }
  };

  if (isLoadingReference || isLoadingNetworks) {
    return <Loader />;
  }

  const activeNetwork = picker?.network ?? null;
  const activeConfig = rows.find(({ config }) => config.network === activeNetwork)?.config ?? null;

  return (
    <>
      <div className="campaign-label">
        Publishers
        <span className="campaign-pill campaign-pill--muted">
          {liveCount} of {rows.length} live
        </span>
      </div>

      {error && <div className="alert alert-danger py-2 small">{error}</div>}

      <div className="campaign-card">
        {rows.map(({ reference, config }) => (
          <PublisherRow
            key={reference.publisher_network}
            network={config.network}
            label={reference.label}
            color={reference.color}
            icon={reference.icon}
            config={config as LaunchNetworkConfig}
            resourceName={
              resourceNames[config.network_sub_resource_id ?? config.network_resource_id ?? -1] ??
              null
            }
            templateName={templateNames[config.network_template_id ?? -1] ?? null}
            isBusy={busyNetwork === config.network}
            onPickResource={() => setPicker({ kind: "resource", network: config.network })}
            onPickTemplate={() => setPicker({ kind: "template", network: config.network })}
            onInspectLinks={() => setPicker({ kind: "links", network: config.network })}
            onToggle={(nextEnabled) => handleToggle(config.network, nextEnabled)}
          />
        ))}
      </div>

      {picker?.kind === "resource" && activeNetwork && activeConfig && (
        <NetworkResourceSelectorModal
          show
          onClose={() => setPicker(null)}
          network={activeNetwork}
          currentResourceId={activeConfig.network_resource_id ?? null}
          currentSubResourceId={activeConfig.network_sub_resource_id ?? null}
          onResourceSelected={(resourceId, subResourceId) =>
            handleResourceSelected(activeNetwork, resourceId, subResourceId)
          }
        />
      )}

      {picker?.kind === "template" && activeNetwork && activeConfig && (
        <NetworkTemplatePickerModal
          show
          onClose={() => {
            setPicker(null);
            if (!templateWasSelected.current) return;
            templateWasSelected.current = false;
            refreshConfigs().catch(() => setError("Could not refresh publishers."));
          }}
          network={activeNetwork}
          launchId={launchId}
          currentTemplateId={activeConfig.network_template_id ?? null}
          onTemplateSelected={(networkTemplateId) => {
            templateWasSelected.current = true;
            launchActions.updateNetworkConfig(activeNetwork, {
              network_template_id: networkTemplateId,
            });
          }}
        />
      )}

      {picker?.kind === "links" && activeNetwork && (
        <InspectLinksModal
          show
          onClose={() => setPicker(null)}
          network={activeNetwork}
          launchId={launchId}
        />
      )}
    </>
  );
}
